import { useMemo } from "react";

import {
    BsMetaUtil,
    getLoopBorrowCapDetails,
    isLeverageLoop,
    LoopExpanded,
    LoopTokenPriceChange,
    OracleOHLC,
    useLoopTokenPriceChanges,
    useOracleHistoricalPrices
} from "@bridgesplit/abf-react";
import { StatProps, StatRow, StatColumn, MEDIA, useAppPalette, Text } from "@bridgesplit/ui";
import {
    filterUndefined,
    formatMultiplier,
    formatPercent,
    formatUsd,
    nearestMinute,
    isDefined,
    TIME
} from "@bridgesplit/utils";
import { useMediaQuery } from "@mui/material";
import { COPY } from "app/constants";
import { StatWithSubtext, TokenStat, TokenUsdStat } from "app/components/common";

import { useLoopContext } from "../LoopContext";

export function LoopVaultStats() {
    const { loopExpanded } = useLoopContext();
    const { data: priceData } = useOracleHistoricalPrices({
        mints: loopExpanded?.collateralToken ? [loopExpanded.collateralToken.mint] : [],
        startTimestamp: nearestMinute() - TIME.DAY * 7
    });
    const displayAsColumn = useMediaQuery(MEDIA.MD.below);

    if (isLeverageLoop(loopExpanded)) {
        const historicalPrices =
            priceData && loopExpanded?.collateralToken.mint ? priceData[loopExpanded.collateralToken.mint] : undefined;
        return (
            <LeverageLoopVaultStats
                loopExpanded={loopExpanded}
                displayAsColumn={displayAsColumn}
                historicalPrices={historicalPrices}
            />
        );
    } else {
        return <YieldLoopVaultStats loopExpanded={loopExpanded} displayAsColumn={displayAsColumn} />;
    }
}

function LeverageLoopVaultStats({
    loopExpanded,
    historicalPrices,
    displayAsColumn
}: {
    loopExpanded: LoopExpanded | undefined;
    historicalPrices: OracleOHLC[] | undefined;
    displayAsColumn: boolean;
}) {
    const {
        data: [priceChange7d],
        loading: priceChangeLoading
    } = useLoopTokenPriceChanges(!loopExpanded ? undefined : loopExpanded?.collateralToken?.mint, historicalPrices);

    const leverageLoopStats = getLeverageLoopStats(loopExpanded, displayAsColumn, priceChange7d) ?? [];
    const baseStats = getBaseStats(loopExpanded, displayAsColumn) ?? [];
    const stats: StatProps[] = [...leverageLoopStats, ...baseStats].filter(filterUndefined);

    const isLoading = priceChangeLoading || !loopExpanded;

    if (displayAsColumn) {
        return <StatColumn loading={isLoading} stats={stats} />;
    }

    return <StatRow loading={isLoading} stats={stats} />;
}

function YieldLoopVaultStats({
    loopExpanded,
    displayAsColumn
}: {
    loopExpanded: LoopExpanded | undefined;
    displayAsColumn: boolean;
}) {
    const yieldLoopStats = getYieldLoopStats(loopExpanded, displayAsColumn) ?? [];
    const baseStats = getBaseStats(loopExpanded, displayAsColumn) ?? [];
    const stats: StatProps[] = [...yieldLoopStats, ...baseStats].filter(filterUndefined);

    const isLoading = !loopExpanded;

    if (displayAsColumn) {
        return <StatColumn loading={isLoading} stats={stats} />;
    }

    return <StatRow loading={isLoading} stats={stats} />;
}

function getBaseStats(loopExpanded: LoopExpanded | undefined, displayAsColumn: boolean): StatProps[] {
    const borrowCapWarning = getLoopBorrowCapDetails(loopExpanded);

    const availableLiquidity = Math.min(
        loopExpanded?.loopVault?.principalAmountAvailable ?? 0,
        borrowCapWarning?.remainingGlobalCapacity ?? 0
    );

    return [
        {
            value: (
                <StatWithSubtext
                    stat={formatMultiplier(loopExpanded?.loopVault.maxLeverage)}
                    subtext={`Avg: ${formatMultiplier(loopExpanded?.loopVault.wAvgLeverage)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: COPY.MAX_LEVERAGE_TERM
        },
        {
            value: (
                <TokenStat
                    tokenAmount={availableLiquidity}
                    token={loopExpanded?.principalToken}
                    usdPrice={loopExpanded?.principalOracle.usdPrice ?? 0}
                    displayAsColumn={displayAsColumn}
                    warning={borrowCapWarning?.warningMessage ?? undefined}
                />
            ),
            caption: COPY.AVAILABLE_LIQUIDITY_TERM
        },
        {
            value: (
                <TokenUsdStat
                    usdValue={loopExpanded?.loopVault.collateralDepositedUsd ?? 0}
                    token={loopExpanded?.inputToken}
                    usdPrice={
                        loopExpanded
                            ? loopExpanded.loopVault.collateralDepositedUsd / loopExpanded.loopVault.collateralDeposited
                            : 0
                    }
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: COPY.DEPOSITED_TERM,
            tooltip: `The total amount of ${BsMetaUtil.getSymbol(
                loopExpanded?.collateralToken
            )} used as collateral across Loops`
        }
    ];
}

function getLeverageLoopStats(
    loopExpanded: LoopExpanded | undefined,
    displayAsColumn: boolean,
    priceChange7d: LoopTokenPriceChange | undefined
): StatProps[] | undefined {
    const change = priceChange7d?.change;
    const currentPrice = priceChange7d?.end;

    return [
        {
            value: (
                <StatWithSubtext
                    stat={<PriceChange change={change} />}
                    subtext={`Price: ${formatUsd(currentPrice)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: `${BsMetaUtil.getSymbol(loopExpanded?.collateralToken)} 7D Change`
        }
    ];
}

function PriceChange({ change }: { change: number | undefined }) {
    const { textDisabled, error, success } = useAppPalette();

    const color = useMemo(() => {
        if (isDefined(change)) {
            return change > 0 ? success : error;
        }
        return textDisabled;
    }, [change, success, error, textDisabled]);

    return (
        <Text
            variant="h3"
            sx={{
                gap: 0.5,
                color
            }}
        >
            {formatPercent(change, { includePlus: true })}
        </Text>
    );
}

function getYieldLoopStats(loopExpanded: LoopExpanded | undefined, displayAsColumn: boolean): StatProps[] | undefined {
    return [
        {
            value: (
                <StatWithSubtext
                    stat={formatPercent(loopExpanded?.loopVault.maxLeveragedApyPct)}
                    subtext={`Avg: ${formatPercent(loopExpanded?.loopVault.wAvgApy)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: "Max net APY"
        }
    ];
}
